/**
 * Cart Guard - Prevents mixing preorder and regular items
 * Uses aggressive blocking techniques to prevent items from being added in the first place
 */

class CartGuard {
  constructor() {
    this.isPreorderCart = false;
    this.hasRegularItems = false;
    this.cartData = null;
    this.modal = null;
    this.isBlocking = false;
    this.originalFetch = window.fetch;
    this.originalXHR = window.XMLHttpRequest;
    
    // Custom preorder detection patterns (can be configured)
    this.customPreorderPatterns = window.cartGuardConfig?.preorderPatterns || [];
    
    this.init();
  }

  init() {
    this.createModal();
    this.setupGlobalBlocking();
    this.bindEvents();
    
    // Update cart state immediately and after a short delay
    this.updateCartState();
    setTimeout(() => {
      this.updateCartState();
    }, 500);
  }

  /**
   * Set up global request blocking BEFORE any other scripts can make requests
   */
  setupGlobalBlocking() {
    const self = this;
    
    // Override fetch globally - this runs BEFORE any other fetch requests
    window.fetch = async function(url, options = {}) {
      // Check if this is a cart ADD request (not change/update)
      if (typeof url === 'string' && url.includes('/cart/add')) {
        // Update cart state immediately
        await self.updateCartState();
        
        // Check if this would create a mixed cart
        if (await self.shouldBlockRequest(url, options)) {
          self.showModal();
          
          // Throw an error to completely block the request
          throw new Error('Cart Guard: Mixed cart not allowed');
        }
      }
      
      // Allow the request to proceed (including cart/change requests)
      return self.originalFetch.call(this, url, options);
    };

    // Override XMLHttpRequest globally
    const OriginalXHR = window.XMLHttpRequest;
    window.XMLHttpRequest = function() {
      const xhr = new OriginalXHR();
      const originalOpen = xhr.open;
      const originalSend = xhr.send;
      
      xhr.open = function(method, url, ...args) {
        this._url = url;
        this._method = method;
        return originalOpen.apply(this, [method, url, ...args]);
      };
      
      xhr.send = async function(data) {
        if (this._url && this._url.includes('/cart/add')) {
          // Update cart state immediately
          await self.updateCartState();
          
          // Check if this would create a mixed cart
          if (await self.shouldBlockXHRRequest(this._url, data)) {
            self.showModal();
            
            // Abort the request completely
            this.abort();
            return;
          }
        }
        
        return originalSend.call(this, data);
      };
      
      return xhr;
    };

    // Maintain compatibility
    Object.setPrototypeOf(window.XMLHttpRequest, OriginalXHR);
    Object.defineProperty(window.XMLHttpRequest, 'prototype', {
      value: OriginalXHR.prototype,
      writable: false
    });
  }

  /**
   * Create the mixed cart warning modal
   */
  createModal() {
    const modalHTML = `
      <div id="cart-guard-modal" class="cart-guard-modal">
        <div
          role="dialog"
          aria-label="${window.cartGuardTranslations?.mixedCartModalTitle || 'Mixed Cart Not Allowed'}"
          aria-modal="true"
          class="cart-guard-modal__content"
          tabindex="-1"
        >
          <button
            type="button"
            class="modal__close-button cart-guard-modal__close"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close" fill="none" viewBox="0 0 18 17">
              <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"></path>
            </svg>
          </button>
          <div class="cart-guard-modal__content-info">
            <h2 class="cart-guard-modal__title h2">
              ${window.cartGuardTranslations?.mixedCartModalTitle || 'Heads up!'}
            </h2>
            <div class="cart-guard-modal__body">
              <p class="cart-guard-modal__message">
                ${window.cartGuardTranslations?.mixedCartModalMessage || 'Pre-order items must be purchased separately from in-stock products. Please complete your current checkout before adding other items.'}
              </p>
            </div>
            <div class="cart-guard-modal__footer">
              <button type="button" class="cart-guard-modal__button button button--primary">
                ${window.cartGuardTranslations?.mixedCartModalButton || 'Continue Shopping'}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.querySelector('#cart-guard-modal');
    
    // Bind modal close events
    this.modal.querySelector('.cart-guard-modal__close').addEventListener('click', () => this.hideModal());
    this.modal.querySelector('.cart-guard-modal__button').addEventListener('click', () => this.hideModal());
    
    // Close modal when clicking outside
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.hideModal();
      }
    });
  }

  /**
   * Bind comprehensive event listeners with multiple layers of protection
   */
  bindEvents() {
    // LAYER 1: Form submission blocking (capturing phase - runs FIRST)
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action*="/cart/add"]')) {
        this.handleFormSubmit(e);
      }
    }, true); // Capturing phase

    // LAYER 2: Button click blocking (before any form processing)
    document.addEventListener('click', (e) => {
      const button = e.target.closest('button[type="submit"], input[type="submit"], .btn, .button');
      if (button) {
        const form = button.closest('form[action*="/cart/add"]');
        if (form) {
          this.handleButtonClick(e, button, form);
        }
      }
      
      // Enhanced add-to-cart button detection
      const addToCartSelectors = [
        '.quick-add__submit',
        '.product-form__submit', 
        '[data-add-to-cart]',
        '.add-to-cart',
        '.btn--add-to-cart',
        '.product-form__cart-submit',
        '.shopify-payment-button__button',
        '.button--add-to-cart',
        'button[name="add"]',
        'input[name="add"]',
        '.js-btn-add-to-cart'
      ];
      
      for (const selector of addToCartSelectors) {
        if (e.target.matches(selector) || e.target.closest(selector)) {
          this.handleGenericAddToCart(e);
          break;
        }
      }
      
      // Special handling for product-form custom elements
      const productForm = e.target.closest('product-form');
      if (productForm && button && button.type === 'submit') {
        this.handleProductFormSubmit(e, productForm);
      }
    }, true); // Capturing phase

    // LAYER 3: Backup form submission blocking (bubbling phase)
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action*="/cart/add"]')) {
        this.handleFormSubmit(e);
      }
    }, false); // Bubbling phase

    // LAYER 4: Monitor for dynamically added forms
    this.observeForms();

    // LAYER 5: Monitor cart state changes
    this.monitorCartChanges();
  }

  /**
   * Handle form submission with immediate blocking
   */
  handleFormSubmit(e) {
    // Update cart state immediately before checking
    this.updateCartState();
    
    // Block immediately if we should
    if (this.shouldBlockFormSubmission(e.target)) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      this.showModal();
      this.disableForm(e.target);
      return false;
    }
  }

  /**
   * Handle button clicks with immediate blocking
   */
  handleButtonClick(e, button, form) {
    // Update cart state immediately before checking
    this.updateCartState();
    
    // Block immediately if we should
    if (this.shouldBlockFormSubmission(form)) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Disable the button immediately
      button.disabled = true;
      setTimeout(() => {
        button.disabled = false;
      }, 5000);
      
      this.showModal();
      this.disableForm(form);
      return false;
    }
  }

  /**
   * Handle generic add-to-cart events
   */
  handleGenericAddToCart(e) {

    
    const form = e.target.closest('form[action*="/cart/add"]');
    if (form && this.shouldBlockFormSubmission(form)) {

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      this.showModal();
      this.disableForm(form);
      return false;
    }
  }

  /**
   * Handle product-form custom element submissions
   */
  handleProductFormSubmit(e, productForm) {

    
    const form = productForm.querySelector('form');
    if (form && this.shouldBlockFormSubmission(form)) {
      
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      this.disableForm(form);
      this.showModal();
      return false;
    }
  }

  /**
   * Check if a fetch request should be blocked
   */
  shouldBlockRequest(url, options) {
    try {
      // Skip blocking if we're on a protected route
      if (this.hasRouteProtection()) {
        if (window.cartGuardConfig?.debug) {
          console.log('Cart Guard: Skipping request blocking - protected route');
        }
        return false;
      }

      // Parse the request to understand what's being added
      let isPreorderProduct = false;
      
      if (options.body) {
        const formData = new URLSearchParams(options.body);
        const variantId = formData.get('id');
        
        // Check if this is an insurance-related request
        if (this.isInsuranceRequest(formData)) {
          if (window.cartGuardConfig?.debug) {
            console.log('Cart Guard: Skipping request blocking - insurance item');
          }
          return false;
        }
        
        if (variantId) {
          isPreorderProduct = this.checkIfVariantIsPreorder(variantId);
        }
      }
      
      return this.wouldCreateMixedCart(null, isPreorderProduct);
    } catch (error) {
      console.warn('Cart Guard: Error checking request blocking:', error);
      return false;
    }
  }

  /**
   * Check if a request contains insurance-related data
   */
  isInsuranceRequest(formData) {
    if (!formData) return false;
    
    // Check for insurance-related form data
    for (const [key, value] of formData.entries()) {
      const keyLower = key.toLowerCase();
      const valueLower = typeof value === 'string' ? value.toLowerCase() : '';
      
      if (keyLower.includes('insurance') || 
          keyLower.includes('protection') ||
          keyLower.includes('coverage') ||
          valueLower.includes('insurance') ||
          valueLower.includes('protection') ||
          valueLower.includes('coverage')) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Check if an XHR request should be blocked
   */
  shouldBlockXHRRequest(url, data) {
    try {
      // Skip blocking if we're on a protected route
      if (this.hasRouteProtection()) {
        if (window.cartGuardConfig?.debug) {
          console.log('Cart Guard: Skipping XHR blocking - protected route');
        }
        return false;
      }

      // Parse the request to understand what's being added
      let isPreorderProduct = false;
      
      if (data) {
        const formData = new URLSearchParams(data);
        
        // Check if this is an insurance-related request
        if (this.isInsuranceRequest(formData)) {
          if (window.cartGuardConfig?.debug) {
            console.log('Cart Guard: Skipping XHR blocking - insurance item');
          }
          return false;
        }
        
        const variantId = formData.get('id');
        
        if (variantId) {
          isPreorderProduct = this.checkIfVariantIsPreorder(variantId);
        }
      }
      
      return this.wouldCreateMixedCart(null, isPreorderProduct);
    } catch (error) {
      console.warn('Cart Guard: Error checking XHR blocking:', error);
      return false;
    }
  }

  /**
   * Check if form submission should be blocked
   */
  shouldBlockFormSubmission(form) {
    if (!form || this.hasRouteProtection()) {
      return false;
    }
    
    // Check if this form contains insurance-related data
    const formData = new FormData(form);
    if (this.isInsuranceRequest(formData)) {
      if (window.cartGuardConfig?.debug) {
        console.log('Cart Guard: Skipping form blocking - insurance item');
      }
      return false;
    }
    
    return this.wouldCreateMixedCart(form);
  }

  /**
   * Determine if adding this product would create a mixed cart
   */
  wouldCreateMixedCart(form, isPreorderProduct = null) {
    // If we don't have cart data yet, be conservative and allow
    if (!this.cartData) {
      return false;
    }
    
    // Determine if the product being added is a preorder
    if (isPreorderProduct === null && form) {
      isPreorderProduct = this.isProductPreorder(form);
    }
    

    
    // If cart is empty, allow any product
    if (!this.cartData || this.cartData.item_count === 0) {
      return false;
    }
    
    // Block if trying to add preorder to regular cart
    if (isPreorderProduct && this.hasRegularItems) {

      return true;
    }
    
    // Block if trying to add regular to preorder cart
    if (!isPreorderProduct && this.isPreorderCart) {

      return true;
    }
    
    return false;
  }

  /**
   * Check if a product/form represents a preorder item
   */
  isProductPreorder(element) {
    if (!element) return false;
    

    
    // Method 1: Check form data attributes (set by liquid templates)
    if (element.dataset && element.dataset.preorder === 'true') {

      return true;
    }
    
    // Method 2: Check button data attributes (set by liquid templates)
    const submitButtons = element.querySelectorAll('button[type="submit"], input[type="submit"], .button, .btn');
    for (const button of submitButtons) {
      if (button.dataset && button.dataset.preorder === 'true') {

        return true;
      }
      
      if (button.classList.contains('button--preorder')) {

        return true;
      }
    }
    
    // Method 3: Check parent elements for preorder indicators
    let parent = element.parentElement;
    while (parent && parent !== document.body) {
      if (parent.dataset && parent.dataset.preorder === 'true') {
        return true;
      }
      parent = parent.parentElement;
    }
    
    return false;
  }

  /**
   * Check if an item is an insurance product that should be excluded from cart-guard analysis
   */
  isInsuranceItem(item) {
    if (!item) return false;
    
    // Check product type
    if (item.product_type && item.product_type.toLowerCase().includes('insurance')) {
      return true;
    }
    
    // Check product title
    if (item.product_title && item.product_title.toLowerCase().includes('insurance')) {
      return true;
    }
    
    // Check title fallback
    if (item.title && item.title.toLowerCase().includes('insurance')) {
      return true;
    }
    
    // Check properties for insurance indicators
    if (item.properties) {
      for (const [key, value] of Object.entries(item.properties)) {
        const keyLower = key.toLowerCase();
        const valueLower = typeof value === 'string' ? value.toLowerCase() : '';
        
        if (keyLower.includes('insurance') || 
            keyLower.includes('protection') ||
            keyLower.includes('coverage') ||
            valueLower.includes('insurance') ||
            valueLower.includes('protection') ||
            valueLower.includes('coverage')) {
          return true;
        }
      }
    }
    
    // Check vendor
    if (item.vendor && item.vendor.toLowerCase().includes('insurance')) {
      return true;
    }
    
    // Check handle
    if (item.handle && item.handle.toLowerCase().includes('insurance')) {
      return true;
    }
    
    return false;
  }

  /**
   * Check if we're on a route that should be protected from cart guard
   */
  hasRouteProtection() {
    const path = window.location.pathname;
    const protectedRoutes = ['/cart', '/checkout'];
    
    // Enhanced protection: also check for checkout processes and insurance-related flows
    const enhancedProtection = [
      '/checkout',
      '/cart',
      'checkout',
      'thank_you',
      'orders',
      'processing'
    ];
    
    // Check if we're in any protected flow
    const isProtected = enhancedProtection.some(route => path.includes(route));
    
    // Also check for checkout-related URL parameters or hash
    const urlParams = new URLSearchParams(window.location.search);
    const hasCheckoutParams = urlParams.has('checkout') || 
                             urlParams.has('step') || 
                             urlParams.has('insurance');
    
    // Check hash for checkout-related fragments  
    const hasCheckoutHash = window.location.hash.includes('checkout') ||
                           window.location.hash.includes('step');
    
    return isProtected || hasCheckoutParams || hasCheckoutHash;
  }

  /**
   * Update cart state from Shopify
   */
  async updateCartState() {
    try {
      const response = await this.originalFetch.call(window, '/cart.js');
      this.cartData = await response.json();
      await this.analyzeCartContents();

    } catch (error) {
      console.warn('Cart Guard: Failed to update cart state:', error);
      this.cartData = null;
      this.isPreorderCart = false;
      this.hasRegularItems = false;
    }
  }

  /**
   * Analyze cart contents to determine cart type
   */
  async analyzeCartContents() {
    if (!this.cartData || !this.cartData.items) {
      this.isPreorderCart = false;
      this.hasRegularItems = false;
      return;
    }

    let preorderCount = 0;
    let regularCount = 0;


    for (const item of this.cartData.items) {
      // Skip insurance items from cart-guard analysis
      if (this.isInsuranceItem(item)) {
        if (window.cartGuardConfig?.debug) {
          console.log('Cart Guard: Skipping insurance item from analysis:', item.product_title || item.title);
        }
        continue;
      }

      // Check if item has preorder tag
      let isPreorder = false;
      let detectionMethod = 'none';
      

      
             // Method 1: Check line item properties for preorder indicator (most reliable)
       if (item.properties && item.properties._preorder === 'true') {
         isPreorder = true;
         detectionMethod = 'line_item_properties';
       }
      
      // Method 2: Check product title
      if (!isPreorder && item.product_title) {
        const title = item.product_title.toLowerCase();
        isPreorder = title.includes('pre-order') || 
                    title.includes('preorder') ||
                    title.includes('pre order');
        if (isPreorder) {
          detectionMethod = 'product_title';
        }
      }
      
      // Method 3: Check variant title
      if (!isPreorder && item.variant_title) {
        const variantTitle = item.variant_title.toLowerCase();
        isPreorder = variantTitle.includes('pre-order') || 
                    variantTitle.includes('preorder') ||
                    variantTitle.includes('pre order');
        if (isPreorder) {
          detectionMethod = 'variant_title';
        }
      }
      
      // Method 4: Check properties for preorder indicators
      if (!isPreorder && item.properties) {
        for (const [key, value] of Object.entries(item.properties)) {
          const keyLower = key.toLowerCase();
          const valueLower = typeof value === 'string' ? value.toLowerCase() : '';
          
          if (keyLower.includes('preorder') || 
              keyLower.includes('pre-order') ||
              keyLower.includes('pre order') ||
              valueLower.includes('preorder') ||
              valueLower.includes('pre-order') ||
              valueLower.includes('pre order')) {
            isPreorder = true;
            detectionMethod = 'properties';
            break;
          }
        }
      }
      
      // Method 5: Check product handle
      if (!isPreorder && item.handle) {
        const handle = item.handle.toLowerCase();
        isPreorder = handle.includes('pre-order') || 
                    handle.includes('preorder') ||
                    handle.includes('pre order');
        if (isPreorder) {
          detectionMethod = 'handle';
        }
      }
      
      // Method 6: Check vendor
      if (!isPreorder && item.vendor) {
        const vendor = item.vendor.toLowerCase();
        isPreorder = vendor.includes('pre-order') || 
                    vendor.includes('preorder') ||
                    vendor.includes('pre order');
        if (isPreorder) {
          detectionMethod = 'vendor';
        }
      }
      
      // Method 7: Check product type
      if (!isPreorder && item.product_type) {
        const productType = item.product_type.toLowerCase();
        isPreorder = productType.includes('pre-order') || 
                    productType.includes('preorder') ||
                    productType.includes('pre order');
        if (isPreorder) {
          detectionMethod = 'product_type';
        }
      }
      
      // Method 8: Check URL
      if (!isPreorder && item.url) {
        const url = item.url.toLowerCase();
        isPreorder = url.includes('pre-order') || 
                    url.includes('preorder') ||
                    url.includes('pre order');
        if (isPreorder) {
          detectionMethod = 'url';
        }
      }
      
             // Method 9: Fallback - check if this item has specific line item properties that indicate preorder
       if (!isPreorder && item.properties) {
         // Check for common preorder indicators in line item properties
         const propString = JSON.stringify(item.properties).toLowerCase();
         if (propString.includes('preorder') || 
             propString.includes('pre-order') ||
             propString.includes('pre order')) {
           isPreorder = true;
           detectionMethod = 'properties_fallback';
         }
       }
       
       // Method 10: Custom patterns check
       if (!isPreorder && this.customPreorderPatterns.length > 0) {
         for (const pattern of this.customPreorderPatterns) {
           const itemString = JSON.stringify(item).toLowerCase();
           if (itemString.includes(pattern.toLowerCase())) {
             isPreorder = true;
             detectionMethod = 'custom_pattern';
             break;
           }
         }
       }

      
      if (isPreorder) {
        preorderCount++;
      } else {
        regularCount++;
      }
    }

    this.isPreorderCart = preorderCount > 0;
    this.hasRegularItems = regularCount > 0;
    

  }

  /**
   * Check if a variant is a preorder by checking current page data attributes
   */
  checkIfVariantIsPreorder(variantId) {
    // Check if we're on a product page with preorder indicators
    const productForms = document.querySelectorAll('form[data-type="add-to-cart-form"]');
    for (const form of productForms) {
      if (form.dataset.preorder === 'true') {
        return true;
      }
    }
    
    // Check for preorder buttons
    const preorderButtons = document.querySelectorAll('button[data-preorder="true"], .button--preorder');
    if (preorderButtons.length > 0) {
      return true;
    }
    
    return false;
  }

  /**
   * Monitor for dynamically added forms
   */
  observeForms() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node is a form or contains forms
            const forms = node.matches && node.matches('form[action*="/cart/add"]') 
              ? [node] 
              : node.querySelectorAll ? node.querySelectorAll('form[action*="/cart/add"]') : [];
            
            forms.forEach((form) => {
              // The global event listeners will catch these automatically
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Monitor cart changes and update state
   */
  monitorCartChanges() {
    // Update cart state periodically
    setInterval(() => {
      this.updateCartState();
    }, 1000); // Check every second for changes
    
    // Also listen for cart-related events
    document.addEventListener('cart:updated', () => {
      this.updateCartState();
    });
    
    // Listen for page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.updateCartState();
      }
    });
    
    // Listen for navigation events to update cart state
    document.addEventListener('DOMContentLoaded', () => {
      this.updateCartState();
    });
    
    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', () => {
      setTimeout(() => {
        this.updateCartState();
      }, 100);
    });
    
    // Listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function(...args) {
      originalPushState.apply(this, args);
      setTimeout(() => {
        window.cartGuard?.updateCartState();
      }, 100);
    };
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(this, args);
      setTimeout(() => {
        window.cartGuard?.updateCartState();
      }, 100);
    };
  }

  /**
   * Temporarily disable a form to prevent submission
   */
  disableForm(form, duration = 5000) {
    const elements = form.querySelectorAll('button, input[type="submit"]');
    
    elements.forEach(el => {
      el.disabled = true;
      el.style.opacity = '0.5';
      el.style.cursor = 'not-allowed';
    });
    
    setTimeout(() => {
      elements.forEach(el => {
        el.disabled = false;
        el.style.opacity = '';
        el.style.cursor = '';
      });
    }, duration);
  }

  /**
   * Show the mixed cart modal
   */
  showModal() {
    if (this.modal) {
      this.modal.style.display = 'flex';
      this.modal.setAttribute('open', '');
      
      // Focus the modal for accessibility
      const focusTarget = this.modal.querySelector('.cart-guard-modal__content');
      if (focusTarget) {
        focusTarget.focus();
      }
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      
    } else {
      console.warn('Cart Guard: Modal not found, recreating...');
      this.createModal();
      if (this.modal) {
        this.showModal();
      }
    }
  }

  /**
   * Hide the mixed cart modal
   */
  hideModal() {
    if (this.modal) {
      this.modal.style.display = 'none';
      this.modal.removeAttribute('open');
      
      // Restore body scroll
      document.body.style.overflow = '';
      
    }
  }
}

// Initialize cart guard when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartGuard = new CartGuard();
  });
} else {
  window.cartGuard = new CartGuard();
} 